import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, CalendarIcon, MailIcon, SmartphoneIcon, CalendarDaysIcon, UsersIcon, BuildingIcon, PercentIcon, DollarSignIcon, AlertCircleIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon, UserIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { SearchableSelect } from './SearchableSelect';
import { mockSuppliers } from '../utils/mockSuppliers';
export interface Recipient {
  id: number;
  email: string;
  phone: string;
}
export interface Supplier {
  id: number;
  name: string;
  email: string;
  amount: string;
  percentage?: string;
}
export interface Installment {
  id: number;
  amount: string;
  dueDate: string;
  sendLinkDate: string;
  description: string;
  recipient?: Recipient;
}
interface InstallmentPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onSplitTypeChange?: (type: 'dates' | 'recipients' | 'commission') => void;
  onInstallmentsChange?: (installments: Installment[]) => void;
  onSuppliersChange?: (suppliers: Supplier[]) => void;
  onBeneficiaryTypeChange?: (type: 'percentage' | 'fixed') => void;
  onTotalPaymentAmountChange?: (amount: string) => void;
}
export function InstallmentPayment({
  onExpiryDateChange,
  onSplitTypeChange,
  onInstallmentsChange,
  onSuppliersChange,
  onBeneficiaryTypeChange,
  onTotalPaymentAmountChange
}: InstallmentPaymentProps) {
  const [installments, setInstallments] = useState<Installment[]>([{
    id: 1,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 1,
      email: '',
      phone: ''
    }
  }, {
    id: 2,
    amount: '',
    dueDate: '',
    sendLinkDate: '',
    description: '',
    recipient: {
      id: 2,
      email: '',
      phone: ''
    }
  }]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([{
    id: 1,
    name: '',
    email: '',
    amount: '',
    percentage: '0'
  }]);
  const [splitType, setSplitType] = useState<'dates' | 'recipients' | 'commission'>('dates');
  const [beneficiaryType, setBeneficiaryType] = useState<'percentage' | 'fixed'>('percentage');
  const [feeType, setFeeType] = useState<'supplier' | 'merchant' | 'split' | 'customer'>('supplier');
  const [passFeesToCustomer, setPassFeesToCustomer] = useState(false);
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [totalPaymentAmount, setTotalPaymentAmount] = useState('');
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [supplierCommissionTotal, setSupplierCommissionTotal] = useState(0);
  const [showDatePickers, setShowDatePickers] = useState<{
    [key: number]: {
      due: boolean;
      send: boolean;
    };
  }>({});
  // This would come from settings in a real app
  const defaultExpiryDays = 30;
  // Validate commission amounts don't exceed total payment
  useEffect(() => {
    if (splitType === 'commission' && totalPaymentAmount) {
      const totalAmount = parseFloat(totalPaymentAmount) || 0;
      // Calculate total commission amount
      const commissionTotal = suppliers.reduce((total, supplier) => {
        if (beneficiaryType === 'percentage') {
          // For percentage, we'll check in another validation
          return total;
        } else {
          // For fixed amount
          return total + (parseFloat(supplier.amount) || 0);
        }
      }, 0);
      setSupplierCommissionTotal(commissionTotal);
      if (beneficiaryType === 'fixed' && commissionTotal > totalAmount) {
        setCommissionError('Total commission amount cannot exceed the total payment amount');
      } else if (beneficiaryType === 'percentage') {
        const percentageTotal = suppliers.reduce((total, supplier) => {
          return total + (parseFloat(supplier.percentage || '0') || 0);
        }, 0);
        if (percentageTotal > 100) {
          setCommissionError('Total commission percentage cannot exceed 100%');
        } else {
          setCommissionError(null);
        }
      } else {
        setCommissionError(null);
      }
    }
  }, [splitType, totalPaymentAmount, suppliers, beneficiaryType]);
  // Report state changes to parent
  useEffect(() => {
    onSplitTypeChange?.(splitType);
  }, [splitType]);
  useEffect(() => {
    onInstallmentsChange?.(installments);
  }, [installments]);
  useEffect(() => {
    onSuppliersChange?.(suppliers);
  }, [suppliers]);
  useEffect(() => {
    onBeneficiaryTypeChange?.(beneficiaryType);
  }, [beneficiaryType]);
  useEffect(() => {
    onTotalPaymentAmountChange?.(totalPaymentAmount);
  }, [totalPaymentAmount]);
  // Calculate send link date when due date changes
  const calculateSendLinkDate = (dueDateStr: string): string => {
    if (!dueDateStr) return '';
    try {
      // Parse the due date
      const [month, day, year] = dueDateStr.split('/').map(part => parseInt(part));
      const dueDate = new Date(year, month - 1, day);
      // Subtract the default expiry days
      const sendDate = new Date(dueDate);
      sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
      // Format the date as MM/DD/YYYY
      const sendMonth = (sendDate.getMonth() + 1).toString().padStart(2, '0');
      const sendDay = sendDate.getDate().toString().padStart(2, '0');
      const sendYear = sendDate.getFullYear();
      return `${sendMonth}/${sendDay}/${sendYear}`;
    } catch (error) {
      // If there's any error parsing, return empty string
      return '';
    }
  };
  const addInstallment = () => {
    const newId = Math.max(...installments.map(i => i.id), 0) + 1;
    setInstallments([...installments, {
      id: newId,
      amount: '',
      dueDate: '',
      sendLinkDate: '',
      description: '',
      recipient: {
        id: newId,
        email: '',
        phone: ''
      }
    }]);
  };
  const addSupplier = () => {
    const newId = Math.max(...suppliers.map(s => s.id), 0) + 1;
    setSuppliers([...suppliers, {
      id: newId,
      name: '',
      email: '',
      amount: '',
      percentage: '0'
    }]);
  };
  const removeInstallment = (id: number) => {
    if (installments.length > 1) {
      setInstallments(installments.filter(i => i.id !== id));
    }
  };
  const removeSupplier = (id: number) => {
    if (suppliers.length > 1) {
      setSuppliers(suppliers.filter(s => s.id !== id));
    }
  };
  const updateInstallment = (id: number, field: string, value: string) => {
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updatedInstallment = {
          ...installment,
          [field]: value
        };
        // If we're updating the due date and it's a valid date, update the send link date
        if (field === 'dueDate' && value) {
          updatedInstallment.sendLinkDate = calculateSendLinkDate(value);
        }
        return updatedInstallment;
      }
      return installment;
    }));
  };
  const updateSupplier = (id: number, field: string, value: string) => {
    setSuppliers(suppliers.map(supplier => supplier.id === id ? {
      ...supplier,
      [field]: value
    } : supplier));
  };
  const updateRecipient = (installmentId: number, field: 'email' | 'phone', value: string) => {
    setInstallments(installments.map(installment => installment.id === installmentId && installment.recipient ? {
      ...installment,
      recipient: {
        ...installment.recipient,
        [field]: value
      }
    } : installment));
  };
  const splitOptions = [{
    value: 'dates',
    label: 'By payment schedule',
    icon: <CalendarDaysIcon size={16} data-id="element-2889" />
  }, {
    value: 'recipients',
    label: 'By multiple people',
    icon: <UsersIcon size={16} data-id="element-2890" />
  }, {
    value: 'commission',
    label: 'By commission',
    icon: <PercentIcon size={16} data-id="element-2891" />
  }];
  const beneficiaryOptions = [{
    value: 'percentage',
    label: 'Percentage',
    icon: <PercentIcon size={16} data-id="element-2892" />
  }, {
    value: 'fixed',
    label: 'Fixed amount',
    icon: <DollarSignIcon size={16} data-id="element-2893" />
  }];
  const totalAmount = installments.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const supplierOptions = mockSuppliers.map(s => s.name);
  // Mock suppliers list for dropdown
  const suppliersList = [{
    id: 1,
    name: 'ABC Suppliers',
    email: 'abc@example.com'
  }, {
    id: 2,
    name: 'XYZ Merchants',
    email: 'xyz@example.com'
  }, {
    id: 3,
    name: '123 Distributors',
    email: '123@example.com'
  }, {
    id: 4,
    name: 'Global Partners',
    email: 'global@example.com'
  }];
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleTotalPaymentAmountChange = (value: string) => {
    setTotalPaymentAmount(value);
  };
  const getCommissionAmountDisplay = () => {
    if (beneficiaryType === 'fixed') {
      return `$${supplierCommissionTotal.toFixed(2)} of $${parseFloat(totalPaymentAmount || '0').toFixed(2)}`;
    } else {
      const percentageTotal = suppliers.reduce((total, supplier) => {
        return total + (parseFloat(supplier.percentage || '0') || 0);
      }, 0);
      return `${percentageTotal.toFixed(2)}% of payment`;
    }
  };
  // Date picker helpers
  const toggleDatePicker = (id: number, type: 'due' | 'send') => {
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: prev[id] ? !prev[id][type] : true
      }
    }));
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const handleDateSelect = (id: number, field: 'dueDate' | 'sendLinkDate', date: Date) => {
    // Update in a single state call to avoid stale state overwrites
    setInstallments(installments.map(installment => {
      if (installment.id === id) {
        const updated = {
          ...installment,
          [field]: formatDate(date)
        };
        // If selecting due date, also auto-calculate send link date
        if (field === 'dueDate') {
          const sendDate = new Date(date);
          sendDate.setDate(sendDate.getDate() - defaultExpiryDays);
          updated.sendLinkDate = formatDate(sendDate);
        }
        return updated;
      }
      return installment;
    }));
    // Close the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field === 'dueDate' ? 'due' : 'send']: false
      }
    }));
  };
  const generateCalendar = (dateStr: string) => {
    const today = new Date();
    const selectedDate = dateStr ? parseDate(dateStr) : today;
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        // Fill remaining days of the week
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: selectedDate,
      weeks
    };
  };
  const parseDate = (dateString: string): Date => {
    if (!dateString) return new Date();
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date, selectedDateStr: string): boolean => {
    if (!selectedDateStr) return false;
    const selectedDate = parseDate(selectedDateStr);
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handlePrevMonth = (calendar: any, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() - 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const handleNextMonth = (calendar: any, id: number, type: 'due' | 'send') => {
    const date = new Date(calendar.month);
    date.setMonth(date.getMonth() + 1);
    // Force re-render by toggling the date picker
    setShowDatePickers(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: false
      }
    }));
    setTimeout(() => {
      setShowDatePickers(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [type]: true
        }
      }));
    }, 10);
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2';
  return <div className="space-y-5" data-id="element-2894">
      {/* ─── SPLIT PAYMENT CONFIGURATION ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2895">
        <div className="p-6 space-y-6" data-id="element-2896">
          <div data-id="element-2897">
            <label className={labelClass} data-id="element-2898">
              How would you like to split this payment?
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-2899">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'dates' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2900">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={splitType === 'dates'} onChange={() => setSplitType('dates')} data-id="element-2901" />
                <div data-id="element-2902">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2903">
                    <CalendarDaysIcon size={16} className={splitType === 'dates' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-2904" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-2905">
                      Payment schedule
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-2906">
                    Split into multiple payments on different dates
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'recipients' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2907">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={splitType === 'recipients'} onChange={() => setSplitType('recipients')} data-id="element-2908" />
                <div data-id="element-2909">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2910">
                    <UsersIcon size={16} className={splitType === 'recipients' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-2911" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-2912">
                      Multiple people
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-2913">
                    Split the payment between different recipients
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${splitType === 'commission' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2914">
                <input type="radio" name="splitType" className="mt-0.5 h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={splitType === 'commission'} onChange={() => setSplitType('commission')} data-id="element-2915" />
                <div data-id="element-2916">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2917">
                    <PercentIcon size={16} className={splitType === 'commission' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-2918" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-2919">
                      Commission split
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-2920">
                    Distribute payment between your business and partners
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div data-id="element-2921">
            <label className={labelClass} data-id="element-2922">Order reference</label>
            <input type="text" className={inputClass} placeholder="e.g., Order #1234 or Project Name" data-id="element-2923" />
          </div>
        </div>
      </div>

      {/* ─── SPLIT DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-2924">
        <div className="px-6 py-4 flex items-start justify-between border-b border-gray-100" data-id="element-2925">
          <div data-id="element-2926">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-2927">
              {splitType === 'dates' ? 'Payment schedule' : splitType === 'recipients' ? 'Payment recipients' : 'Commission details'}
            </h3>
            {splitType === 'dates' && <p className="text-xs text-gray-500 mt-0.5 max-w-2xl" data-id="element-2928">
                Payment links will be automatically sent on the "Send link on"
                date. If no date is provided, links will default to being sent
                at least {defaultExpiryDays} days before the due date.
              </p>}
            {splitType === 'commission' && <p className="text-xs text-gray-500 mt-0.5" data-id="element-2929">
                Configure supplier commissions and how payments are split
              </p>}
          </div>
          <div className="text-sm font-medium text-gray-500 flex-shrink-0" data-id="element-2930">
            {splitType === 'commission' ? `Commission: ${getCommissionAmountDisplay()}` : `Total: THB ${totalAmount.toFixed(2)}`}
          </div>
        </div>
        <div className="p-6" data-id="element-2931">
          {/* Commission section */}
          {splitType === 'commission' && <div className="space-y-6" data-id="element-2932">
              {/* Commission configuration - moved from top card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2933">
                <div data-id="element-2934">
                  <label className={labelClass} data-id="element-2935">
                    Total payment amount <span className="text-red-400" data-id="element-2936">*</span>
                  </label>
                  <div className="relative" data-id="element-2937">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2938">
                      <span className="text-sm font-medium text-gray-500" data-id="element-2939">
                        THB
                      </span>
                    </div>
                    <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={totalPaymentAmount} onChange={e => handleTotalPaymentAmountChange(e.target.value)} data-id="element-2940" />
                  </div>
                </div>
                <div data-id="element-2941">
                  <label className={labelClass} data-id="element-2942">Commission type</label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-2943">
                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${beneficiaryType === 'percentage' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2944">
                      <input type="radio" name="beneficiaryType" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={beneficiaryType === 'percentage'} onChange={() => setBeneficiaryType('percentage')} data-id="element-2945" />
                      <div className="flex items-center gap-2" data-id="element-2946">
                        <PercentIcon size={14} className={beneficiaryType === 'percentage' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-2947" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-2948">
                          Percentage
                        </span>
                      </div>
                    </label>

                    <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${beneficiaryType === 'fixed' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2949">
                      <input type="radio" name="beneficiaryType" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={beneficiaryType === 'fixed'} onChange={() => setBeneficiaryType('fixed')} data-id="element-2950" />
                      <div className="flex items-center gap-2" data-id="element-2951">
                        <DollarSignIcon size={14} className={beneficiaryType === 'fixed' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-2952" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-2953">
                          Fixed amount
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {commissionError && <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start border border-red-100" data-id="element-2954">
                  <AlertCircleIcon size={18} className="mr-3 mt-0.5 flex-shrink-0" data-id="element-2955" />
                  <span className="text-sm font-medium" data-id="element-2956">{commissionError}</span>
                </div>}

              {suppliers.map((supplier, index) => <div key={supplier.id} className="flex items-start space-x-4 p-5 bg-gray-50 rounded-xl border border-gray-100" data-id="element-2957">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#F7941D]/10 text-[#F7941D] rounded-full font-bold text-sm flex-shrink-0" data-id="element-2958">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-2959">
                    <div data-id="element-2960">
                      <label className={labelClass} data-id="element-2961">Select supplier</label>
                      <SearchableSelect options={supplierOptions} value={supplier.name} onChange={val => {
                  const selected = mockSuppliers.find(s => s.name === val);
                  // Update both name and email in a single state call to avoid stale state
                  setSuppliers(suppliers.map(s => s.id === supplier.id ? {
                    ...s,
                    name: selected ? selected.name : val,
                    email: selected ? selected.email : s.email
                  } : s));
                }} placeholder="Select a supplier" data-id="element-2962" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2963">
                      <div data-id="element-2964">
                        <label className={labelClass} data-id="element-2965">
                          {beneficiaryType === 'percentage' ? 'Commission percentage' : 'Fixed amount'}
                        </label>
                        <div className="relative" data-id="element-2966">
                          {beneficiaryType === 'percentage' ? <>
                              <input type="number" className={inputClass} placeholder="0" min="0" max="100" value={supplier.percentage} onChange={e => updateSupplier(supplier.id, 'percentage', e.target.value)} data-id="element-2967" />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2968">
                                <span className="text-sm font-medium text-gray-500" data-id="element-2969">
                                  %
                                </span>
                              </div>
                            </> : <>
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2970">
                                <span className="text-sm font-medium text-gray-500" data-id="element-2971">
                                  THB
                                </span>
                              </div>
                              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={supplier.amount} onChange={e => updateSupplier(supplier.id, 'amount', e.target.value)} data-id="element-2972" />
                            </>}
                        </div>
                        {beneficiaryType === 'percentage' && parseFloat(supplier.percentage || '0') > 0 && totalPaymentAmount && <div className="text-xs font-medium text-[#F7941D] mt-2" data-id="element-2973">
                              ≈ THB{' '}
                              {(parseFloat(totalPaymentAmount) * parseFloat(supplier.percentage || '0') / 100).toFixed(2)}
                            </div>}
                      </div>
                      <div data-id="element-2974">
                        <label className={labelClass} data-id="element-2975">Description</label>
                        <input type="text" className={inputClass} placeholder="e.g., Commission for services" value={supplier.description || ''} onChange={e => updateSupplier(supplier.id, 'description', e.target.value)} data-id="element-2976" />
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeSupplier(supplier.id)} className="text-gray-400 hover:text-red-500 mt-1 transition-colors" disabled={suppliers.length <= 1} data-id="element-2977">
                    <TrashIcon size={18} data-id="element-2978" />
                  </button>
                </div>)}

              <button type="button" onClick={addSupplier} className="mt-4 flex items-center text-sm font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-2979">
                <PlusIcon size={16} className="mr-1.5" data-id="element-2980" />
                Add another supplier
              </button>
            </div>}

          {/* Dates and Recipients sections */}
          {(splitType === 'dates' || splitType === 'recipients') && <div className="space-y-4" data-id="element-2981">
              {installments.map((installment, index) => <div key={installment.id} className="flex items-start space-x-4 p-5 bg-gray-50 rounded-xl border border-gray-100" data-id="element-2982">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#F7941D]/10 text-[#F7941D] rounded-full font-bold text-sm flex-shrink-0" data-id="element-2983">
                    {index + 1}
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-2984">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2985">
                      <div data-id="element-2986">
                        <label className={labelClass} data-id="element-2987">Amount</label>
                        <div className="relative" data-id="element-2988">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2989">
                            <span className="text-sm font-medium text-gray-500" data-id="element-2990">
                              THB
                            </span>
                          </div>
                          <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={installment.amount} onChange={e => updateInstallment(installment.id, 'amount', e.target.value)} data-id="element-2991" />
                        </div>
                      </div>

                      {splitType === 'dates' ? <>
                          <div className="relative" data-id="element-2992">
                            <label className={labelClass} data-id="element-2993">Due date</label>
                            <div className="relative" data-id="element-2994">
                              <input type="text" className={`${inputClass} pl-10 cursor-pointer`} placeholder="MM/DD/YYYY" value={installment.dueDate} onChange={e => updateInstallment(installment.id, 'dueDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'due')} readOnly data-id="element-2995" />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2996">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-2997" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'due')} data-id="element-2998">
                                {showDatePickers[installment.id]?.due ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-2999" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-3000" />}
                              </div>
                            </div>

                            {/* Date Picker for Due Date */}
                            {showDatePickers[installment.id]?.due && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-full min-w-[280px]" data-id="element-3001">
                                {(() => {
                        const calendar = generateCalendar(installment.dueDate);
                        return <>
                                      <div className="flex justify-between items-center mb-3" data-id="element-3002">
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handlePrevMonth(calendar, installment.id, 'due')} data-id="element-3003">
                                          <ChevronDownIcon size={16} className="transform rotate-90 text-gray-600" data-id="element-3004" />
                                        </button>
                                        <div className="font-semibold text-gray-900 text-sm" data-id="element-3005">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handleNextMonth(calendar, installment.id, 'due')} data-id="element-3006">
                                          <ChevronDownIcon size={16} className="transform -rotate-90 text-gray-600" data-id="element-3007" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium mb-2" data-id="element-3008">
                                        <div className="text-gray-400" data-id="element-3009">Su</div>
                                        <div className="text-gray-400" data-id="element-3010">Mo</div>
                                        <div className="text-gray-400" data-id="element-3011">Tu</div>
                                        <div className="text-gray-400" data-id="element-3012">We</div>
                                        <div className="text-gray-400" data-id="element-3013">Th</div>
                                        <div className="text-gray-400" data-id="element-3014">Fr</div>
                                        <div className="text-gray-400" data-id="element-3015">Sa</div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-3016">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors mx-auto
                                                  ${isSelected(date, installment.dueDate) ? 'bg-[#F7941D] text-white shadow-sm' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.dueDate) ? 'border border-[#F7941D] text-[#F7941D]' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : !isSelected(date, installment.dueDate) ? 'text-gray-700 hover:bg-gray-100' : ''}
                                                `} onClick={() => handleDateSelect(installment.id, 'dueDate', date)} data-id="element-3017">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-3018"></div>))}
                                      </div>
                                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between" data-id="element-3019">
                                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 7);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-3020">
                                          7 days
                                        </button>
                                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 30);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-3021">
                                          30 days
                                        </button>
                                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 90);
                              handleDateSelect(installment.id, 'dueDate', date);
                            }} data-id="element-3022">
                                          90 days
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>

                          <div className="relative" data-id="element-3023">
                            <label className={labelClass} data-id="element-3024">Send link on</label>
                            <div className="relative" data-id="element-3025">
                              <input type="text" className={`${inputClass} pl-10 cursor-pointer`} placeholder="MM/DD/YYYY" value={installment.sendLinkDate} onChange={e => updateInstallment(installment.id, 'sendLinkDate', e.target.value)} onClick={() => toggleDatePicker(installment.id, 'send')} readOnly data-id="element-3026" />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3027">
                                <CalendarIcon size={16} className="text-gray-400" data-id="element-3028" />
                              </div>
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => toggleDatePicker(installment.id, 'send')} data-id="element-3029">
                                {showDatePickers[installment.id]?.send ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-3030" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-3031" />}
                              </div>
                            </div>

                            {/* Date Picker for Send Link Date */}
                            {showDatePickers[installment.id]?.send && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-full min-w-[280px]" data-id="element-3032">
                                {(() => {
                        const calendar = generateCalendar(installment.sendLinkDate);
                        return <>
                                      <div className="flex justify-between items-center mb-3" data-id="element-3033">
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handlePrevMonth(calendar, installment.id, 'send')} data-id="element-3034">
                                          <ChevronDownIcon size={16} className="transform rotate-90 text-gray-600" data-id="element-3035" />
                                        </button>
                                        <div className="font-semibold text-gray-900 text-sm" data-id="element-3036">
                                          {calendar.month.toLocaleString('default', {
                                month: 'long',
                                year: 'numeric'
                              })}
                                        </div>
                                        <button type="button" className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => handleNextMonth(calendar, installment.id, 'send')} data-id="element-3037">
                                          <ChevronDownIcon size={16} className="transform -rotate-90 text-gray-600" data-id="element-3038" />
                                        </button>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium mb-2" data-id="element-3039">
                                        <div className="text-gray-400" data-id="element-3040">Su</div>
                                        <div className="text-gray-400" data-id="element-3041">Mo</div>
                                        <div className="text-gray-400" data-id="element-3042">Tu</div>
                                        <div className="text-gray-400" data-id="element-3043">We</div>
                                        <div className="text-gray-400" data-id="element-3044">Th</div>
                                        <div className="text-gray-400" data-id="element-3045">Fr</div>
                                        <div className="text-gray-400" data-id="element-3046">Sa</div>
                                      </div>
                                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-3047">
                                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors mx-auto
                                                  ${isSelected(date, installment.sendLinkDate) ? 'bg-[#F7941D] text-white shadow-sm' : ''}
                                                  ${isToday(date) && !isSelected(date, installment.sendLinkDate) ? 'border border-[#F7941D] text-[#F7941D]' : ''}
                                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : !isSelected(date, installment.sendLinkDate) ? 'text-gray-700 hover:bg-gray-100' : ''}
                                                `} onClick={() => handleDateSelect(installment.id, 'sendLinkDate', date)} data-id="element-3048">
                                                {date.getDate()}
                                              </button> : <div key={index} className="w-8 h-8" data-id="element-3049"></div>))}
                                      </div>
                                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between" data-id="element-3050">
                                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 7);
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-3051">
                                          7 days
                                        </button>
                                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" onClick={() => {
                              const date = new Date();
                              date.setDate(date.getDate() + 14);
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-3052">
                                          14 days
                                        </button>
                                        <button type="button" className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" onClick={() => {
                              const date = new Date();
                              handleDateSelect(installment.id, 'sendLinkDate', date);
                            }} data-id="element-3053">
                                          Today
                                        </button>
                                      </div>
                                    </>;
                      })()}
                              </div>}
                          </div>
                        </> : <div className="col-span-2" data-id="element-3054">
                          <label className={labelClass} data-id="element-3055">
                            Recipient contact information
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3056">
                            <div className="relative" data-id="element-3057">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3058">
                                <MailIcon size={16} className="text-gray-400" data-id="element-3059" />
                              </div>
                              <input type="email" className={`${inputClass} pl-10`} placeholder="Email address" value={installment.recipient?.email || ''} onChange={e => updateRecipient(installment.id, 'email', e.target.value)} data-id="element-3060" />
                            </div>
                            <div className="relative" data-id="element-3061">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3062">
                                <SmartphoneIcon size={16} className="text-gray-400" data-id="element-3063" />
                              </div>
                              <input type="tel" className={`${inputClass} pl-10`} placeholder="Phone number" value={installment.recipient?.phone || ''} onChange={e => updateRecipient(installment.id, 'phone', e.target.value)} data-id="element-3064" />
                            </div>
                          </div>
                        </div>}
                    </div>

                    <div data-id="element-3065">
                      <label className={labelClass} data-id="element-3066">Description</label>
                      <input type="text" className={inputClass} placeholder={splitType === 'dates' ? 'e.g., First installment' : "e.g., John's share"} value={installment.description} onChange={e => updateInstallment(installment.id, 'description', e.target.value)} data-id="element-3067" />
                    </div>
                  </div>

                  <button type="button" onClick={() => removeInstallment(installment.id)} className="text-gray-400 hover:text-red-500 mt-1 transition-colors" disabled={installments.length <= 1} data-id="element-3068">
                    <TrashIcon size={18} data-id="element-3069" />
                  </button>
                </div>)}

              <button type="button" onClick={addInstallment} className="mt-4 flex items-center text-sm font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-3070">
                <PlusIcon size={16} className="mr-1.5" data-id="element-3071" />
                Add another{' '}
                {splitType === 'dates' ? 'payment date' : 'recipient'}
              </button>
            </div>}
        </div>
      </div>

      {/* ─── FEE CONFIGURATION ─── */}
      {splitType === 'commission' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-3072">
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-3073">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-3074">
              Fee configuration
            </h4>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-3075">
              Choose who pays the transaction processing fees
            </p>
          </div>
          <div className="p-6 space-y-5" data-id="element-3076">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-3077">
              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'supplier' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-3078">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={feeType === 'supplier'} onChange={() => {
              setFeeType('supplier');
              setPassFeesToCustomer(false);
            }} data-id="element-3079" />
                <div data-id="element-3080">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3081">
                    <BuildingIcon size={16} className={feeType === 'supplier' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-3082" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-3083">
                      Supplier pays
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-3084">
                    Fee is deducted from the supplier's commission
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'merchant' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-3085">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={feeType === 'merchant'} onChange={() => {
              setFeeType('merchant');
              setPassFeesToCustomer(false);
            }} data-id="element-3086" />
                <div data-id="element-3087">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3088">
                    <UserIcon size={16} className={feeType === 'merchant' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-3089" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-3090">
                      My business pays
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-3091">
                    Fee is deducted from your share of the payment
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'split' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-3092">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={feeType === 'split'} onChange={() => {
              setFeeType('split');
              setPassFeesToCustomer(false);
            }} data-id="element-3093" />
                <div data-id="element-3094">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3095">
                    <PercentIcon size={16} className={feeType === 'split' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-3096" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-3097">
                      Split the fee
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-3098">
                    Share the fee between your business and the supplier
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${feeType === 'customer' ? 'border-[#F7941D] bg-[#F7941D]/[0.03] ring-1 ring-[#F7941D]' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-3099">
                <input type="radio" name="feeType" className="mt-0.5 h-4 w-4 text-[#F7941D] focus:ring-[#F7941D] border-gray-300" checked={feeType === 'customer'} onChange={() => {
              setFeeType('customer');
              setPassFeesToCustomer(true);
            }} data-id="element-3100" />
                <div data-id="element-3101">
                  <div className="flex items-center gap-2 mb-1" data-id="element-3102">
                    <UsersIcon size={16} className={feeType === 'customer' ? 'text-[#F7941D]' : 'text-gray-500'} data-id="element-3103" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-3104">
                      Customer pays
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-3105">
                    Fee is added to the total amount paid by the customer
                  </p>
                </div>
              </label>
            </div>

            {feeType === 'split' && <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-3106">
                <label className={labelClass} data-id="element-3107">Supplier fee percentage</label>
                <div className="relative w-full sm:w-1/3" data-id="element-3108">
                  <input type="number" className={inputClass} placeholder="50" min="0" max="100" defaultValue="50" data-id="element-3109" />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-3110">
                    <span className="text-sm font-medium text-gray-500" data-id="element-3111">%</span>
                  </div>
                </div>
              </div>}
          </div>
        </div>}

      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} passFeesToCustomer={passFeesToCustomer} onPassFeesToCustomerChange={val => {
      setPassFeesToCustomer(val);
      if (val) {
        setFeeType('customer');
      } else if (feeType === 'customer') {
        setFeeType('supplier');
      }
    }} data-id="element-3112" />
    </div>;
}